import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, PackageCheck, Upload, Check, FileText, RefreshCw } from 'lucide-react';
import { api, uploadFile } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import type { RtoMeta } from './rtoShared';

const MAX_PROOF_BYTES = 5 * 1024 * 1024;            // same limit as a POD on the Tracking scan
const PROOF_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

/** Upload a handover proof into the POD folders. Returns the storage reference. */
export async function uploadProof(file: File, orderType: 'B2B' | 'B2C'): Promise<string> {
  if (!PROOF_TYPES.includes(file.type)) throw new Error('Proof must be a PDF, JPG or PNG file.');
  if (file.size > MAX_PROOF_BYTES) throw new Error('Proof file must be smaller than 5 MB.');
  const { ref } = await uploadFile(file, orderType === 'B2B' ? 'b2b_pod' : 'b2c_pod');
  return ref;
}

const MAX_DAMAGE_BYTES = 20 * 1024 * 1024;
const DAMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm'];

/** Upload a photo or video of a damaged return. Returns the storage reference. */
export async function uploadDamageProof(file: File): Promise<string> {
  if (!DAMAGE_TYPES.includes(file.type)) throw new Error('Damage proof must be a photo (JPG, PNG, WEBP) or a video (MP4, MOV, WEBM).');
  if (file.size > MAX_DAMAGE_BYTES) throw new Error('Damage proof must be smaller than 20 MB.');
  const fd = new FormData();
  fd.append('file', file, file.name || 'damage');
  // A video takes a while on a warehouse connection — the default 30 s would cut it off.
  const res = await api.post('/uploads?category=rto_damage', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180_000 });
  return res.data.ref;
}

/**
 * B2B parcels travel on an LR number and B2C parcels on an AWB; calling both "AWB / LR" on screen
 * leaves the operator to work out which one this row wants. One helper so every place that prints a
 * number says the same thing.
 */
export function waybillLabel(orderType?: string | null): string {
  return String(orderType ?? '').toUpperCase() === 'B2B' ? 'LR Number' : 'AWB Number';
}

export function WarehouseSelect({ meta, value, onChange }: { meta?: RtoMeta; value: number | ''; onChange: (v: number | '') => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')} className="input w-full">
      <option value="">Select GT warehouse…</option>
      {(meta?.branches ?? []).map((b) => (
        <option key={b.id} value={b.id}>{b.name}{b.city ? ` (${b.city})` : ''}</option>
      ))}
    </select>
  );
}

/** "I Received This Order" — nothing changes until the operator confirms. */
export function ReceiveDialog({ open, onClose, order, meta, onDone }: {
  open: boolean; onClose: () => void; meta?: RtoMeta; onDone: () => void;
  order: { id: string; awb?: string | null; statusName?: string } | null;
}) {
  const [branchId, setBranchId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');
  useEffect(() => {
    if (open) { setBranchId(meta?.defaultBranchId ?? ''); setRemarks(''); }
  }, [open, meta?.defaultBranchId]);

  const m = useMutation({
    mutationFn: async () => (await api.post(`/orders/rto/${order!.id}/receive`, { branchId: branchId || undefined, remarks: remarks.trim() || undefined })).data,
    onSuccess: () => { toast.success(`Received at GT warehouse ${String.fromCharCode(8212)} ${order?.awb ?? ''}`); onDone(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not record the receipt'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="Confirm RTO Receipt" maxWidth="md">
      <div className="space-y-4">
        <p className="text-sm text-slate-700">Are you sure you have physically received this RTO shipment at GT Warehouse?</p>
        {order && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <span className="font-mono font-medium text-slate-900">{order.awb}</span>
            {order.statusName && <span className="text-slate-500"> {String.fromCharCode(183)} currently {order.statusName}</span>}
          </div>
        )}
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">GT warehouse *</label>
          <WarehouseSelect meta={meta} value={branchId} onChange={setBranchId} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Remarks (optional)</label>
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={400} className="input w-full" placeholder="e.g. box torn, tape resealed" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => m.mutate()} disabled={!branchId || m.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {m.isPending ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Yes, Received
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Delivered to merchant from the detail view: proof first, then confirm. */
export function HandoverDialog({ open, onClose, order, onDone, proofOptional = false }: {
  open: boolean; onClose: () => void; onDone: () => void;
  order: { id: string; awb?: string | null; orderType: 'B2B' | 'B2C'; merchant?: string | null } | null;
  /**
   * Opened from the LIST, where the point is to record the handover in one click and file the
   * signed copy afterwards. The upload stays on offer — it is simply no longer the thing standing
   * between the operator and a shipment they have already handed over.
   */
  proofOptional?: boolean;
}) {
  const [proofRef, setProofRef] = useState('');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [remarks, setRemarks] = useState('');
  useEffect(() => { if (open) { setProofRef(''); setFileName(''); setRemarks(''); } }, [open]);

  async function pick(file?: File | null) {
    if (!file || !order) return;
    setUploading(true);
    try { setProofRef(await uploadProof(file, order.orderType)); setFileName(file.name); }
    catch (e: any) { toast.error(e?.response?.data?.message ?? e?.message ?? 'Upload failed'); }
    finally { setUploading(false); }
  }

  const m = useMutation({
    mutationFn: async () => (await api.post(`/orders/rto/${order!.id}/proof`, { proofRef: proofRef || undefined, remarks: remarks.trim() || undefined })).data,
    onSuccess: () => { toast.success('Marked delivered to the merchant'); onDone(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not record the handover'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="RTO Delivered to Merchant" subtitle={order?.merchant ?? undefined} maxWidth="md">
      <div className="space-y-4">
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-mono">{order?.awb}</div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">
            Proof of handover {proofOptional ? '(optional' : '* ('}PDF, JPG or PNG, up to 5 MB)
          </label>
          <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:bg-slate-50 text-sm">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : proofRef ? <Check size={15} className="text-emerald-600" /> : <Upload size={15} />}
            <span className={proofRef ? 'text-emerald-700' : 'text-slate-600'}>{proofRef ? fileName : 'Choose file…'}</span>
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          </label>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Remarks (optional)</label>
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={400} className="input w-full" placeholder="e.g. handed to Ramesh at merchant dock" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => m.mutate()} disabled={(!proofOptional && !proofRef) || uploading || m.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {m.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Confirm RTO Delivery
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Re-dispatch — this returned consignment goes out again under a new waybill.
 *
 * TWO routes, because operators arrive here in two different states:
 *
 *   1. **Create it now** (the default). Opens the ordinary Create Order screen — B2B or B2C to match
 *      the RETURNED shipment, so a B2B return never lands on the B2C form — prefilled from the
 *      original through the same `duplicateFrom` path a duplicate uses. When the new order saves,
 *      it links itself back to this one automatically. Nothing is retyped and nothing is guessed.
 *   2. **Link one already created.** Unchanged: type the new waybill and the matched shipment is
 *      shown before the link is made.
 *
 * Service type is asked for on route 2 rather than inferred, because the same number can exist as an
 * AWB on the B2C side and an LR on the B2B side, and linking the wrong shipment is worse than
 * refusing.
 */
export function RedispatchDialog({ open, onClose, order, onDone }: {
  open: boolean; onClose: () => void; onDone: () => void;
  order: { id: string; awb?: string | null; orderType: 'B2B' | 'B2C'; merchant?: string | null } | null;
}) {
  const nav = useNavigate();
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [orderType, setOrderType] = useState<'B2B' | 'B2C'>('B2C');
  const [awbNo, setAwbNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loadingSource, setLoadingSource] = useState(false);
  useEffect(() => {
    if (open) { setMode('create'); setAwbNo(''); setRemarks(''); setOrderType(order?.orderType ?? 'B2C'); }
  }, [open, order?.orderType]);

  /**
   * Open the create form, prefilled from the returned shipment.
   *
   * The FULL order is fetched first: the RTO list row carries a summary (consignee, city, courier)
   * and the create form needs the rest — products, dimensions, invoice, warehouse. Prefilling from
   * the summary would silently drop them and the replacement would ship wrong.
   */
  const createReplacement = async () => {
    if (!order) return;
    setLoadingSource(true);
    try {
      const src = (await api.get(`/orders/${order.id}`)).data;
      const isB2b = (order.orderType ?? 'B2C') === 'B2B';
      nav(isB2b ? '/orders-b2b/new' : '/orders-b2c/new', {
        state: {
          duplicateFrom: src,
          redispatchFrom: order.id,
          redispatchRemarks: remarks.trim() || undefined,
        },
      });
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not load the original order to copy from');
    } finally {
      setLoadingSource(false);
    }
  };

  /*
   * Look the replacement up as the number is typed, and show what was found.
   *
   * The operator is linking a SHIPMENT, not a number, and a typo that happens to match a real order
   * is the failure that matters — it is silent, and it puts the wrong consignment on the record.
   * Debounced so typing does not fire a request per keystroke, and it runs the same match and the
   * same refusals the link itself will, so the preview cannot be kinder than the outcome.
   */
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(awbNo.trim()), 350); return () => clearTimeout(t); }, [awbNo]);
  const { data: found, isFetching: looking } = useQuery({
    queryKey: ['redispatch-lookup', order?.id, debounced, orderType],
    queryFn: async () => (await api.get(`/orders/rto/${order!.id}/redispatch-lookup`, { params: { awbNo: debounced, orderType } })).data as any,
    enabled: mode === 'link' && !!order && debounced.length >= 3,
  });

  const m = useMutation({
    mutationFn: async () =>
      (await api.post(`/orders/rto/${order!.id}/redispatch`, { awbNo: awbNo.trim(), orderType, remarks: remarks.trim() })).data,
    onSuccess: (r: any) => { toast.success(r?.message ?? 'Re-dispatched'); onDone(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not re-dispatch'),
  });

  const ready = awbNo.trim().length > 0 && remarks.trim().length > 0;

  return (
    <Modal isOpen={open} onClose={onClose} title="Re-dispatch this shipment" subtitle={order?.merchant ?? undefined} maxWidth="md">
      <div className="space-y-4">
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-slate-500">Returned shipment · {waybillLabel(order?.orderType)}</div>
          <div className="font-mono text-slate-800">{order?.awb}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {([
            ['create', 'Create it now', 'Opens the ' + (order?.orderType ?? 'B2C') + ' form, prefilled'],
            ['link', 'Link one already created', 'Enter its AWB / LR number'],
          ] as const).map(([k, title, hint]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              className={`text-left px-3 py-2 rounded-lg border text-sm ${mode === k ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50'}`}>
              <div className="font-medium text-slate-800">{title}</div>
              <div className="text-[11px] text-slate-500">{hint}</div>
            </button>
          ))}
        </div>

        {mode === 'create' ? (
          <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            The <b>{order?.orderType ?? 'B2C'}</b> create form opens with this shipment's consignee, products,
            dimensions and warehouse already filled in — only the waybill is left blank, because that number
            belongs to the returned parcel. Once the new order saves, the two are linked automatically.
          </div>
        ) : (
        <>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">The replacement shipped as *</label>
          <div className="flex gap-2">
            {(['B2C', 'B2B'] as const).map((t) => (
              <button key={t} type="button" onClick={() => setOrderType(t)}
                className={`px-4 py-1.5 rounded-lg border text-sm ${orderType === t ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">New {waybillLabel(orderType)} *</label>
          <input value={awbNo} onChange={(e) => setAwbNo(e.target.value)} maxLength={60}
            className="input w-full font-mono" placeholder={`${waybillLabel(orderType)} of the replacement shipment`} />
          <div className="text-[11px] text-slate-400 mt-0.5">
            Create the replacement order first. This links the two so both halves of the journey read together.
          </div>

          {/* What that number actually is — shown before the link is made, not after. */}
          {debounced.length >= 3 && (
            looking ? (
              <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Looking it up…</div>
            ) : found?.found ? (
              <div className="mt-2 border border-emerald-200 bg-emerald-50/60 rounded-lg px-3 py-2 text-xs">
                <div className="font-medium text-emerald-900">
                  {waybillLabel(found.order.orderType)} <span className="font-mono">{found.order.lrNo || found.order.awbNo || '—'}</span>
                </div>
                <div className="text-slate-700">Order {found.order.orderNo} · {found.order.orderType} · {found.order.status}</div>
                <div className="text-slate-700 mt-0.5">{found.order.customerName}{found.order.customerCity ? ` — ${found.order.customerCity}` : ''}{found.order.customerState ? `, ${found.order.customerState}` : ''}</div>
                <div className="text-slate-600 mt-0.5">
                  {found.order.merchant ?? '—'}{found.order.courier ? ` · ${found.order.courier}` : ''}
                  {found.order.deliveryType ? ` · ${found.order.deliveryType}` : ''}
                  {found.order.chargeableWeight ? ` · ${Number(found.order.chargeableWeight)} kg` : ''}
                </div>
                <div className="text-slate-600">
                  {found.order.invoiceValue != null ? `Invoice ₹${Number(found.order.invoiceValue).toLocaleString('en-IN')}` : ''}
                  {Number(found.order.codValue) > 0 ? ` · COD ₹${Number(found.order.codValue).toLocaleString('en-IN')}` : ''}
                </div>
              </div>
            ) : (
              <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-[11px] text-amber-900">
                {found?.reason ?? 'Not found.'}
              </div>
            )
          )}
        </div>
        </>
        )}

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Remarks {mode === 'link' ? '*' : '(optional)'}</label>
          <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={2000} rows={3}
            className="input w-full resize-none" placeholder="Why is it going out again?" />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          {/* Disabled until the lookup has actually found the shipment — the server would refuse
              anyway, and a button that submits a known-bad value only wastes a round trip. */}
          {mode === 'create' ? (
            <button onClick={createReplacement} disabled={loadingSource}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {loadingSource ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Create {order?.orderType ?? 'B2C'} replacement
            </button>
          ) : (
            <button onClick={() => m.mutate()} disabled={!ready || m.isPending || looking || !found?.found}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {m.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-dispatch
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
